'use client';

import { useRouter } from 'next/navigation';
import { AlertTriangle, LayoutGrid } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCompactUsd } from '../data/mock-prices';

interface TileValueNavProps {
  /**
   * Sum of self-assessed prices across all seats. Lobby values are
   * already USD-denominated (USDC-style snapshot), so we render this
   * directly as dollars — no token → USD conversion needed.
   */
  tileValue: string;
  /**
   * Coin symbol previously displayed inside the legacy modal header.
   * The new `/tiles` view paints its own header so the symbol is
   * unused at this layer; kept on the prop bag for back-compat with
   * call-sites that still forward it (`LobbyNav`). The unused
   * binding is harmless and signals intent — once those call sites
   * stop forwarding it the prop can be dropped.
   */
  coinSymbol?: string;
  /**
   * Risk tier for the whole portfolio — drives the alert styling below.
   * - `safe`    default amber treatment
   * - `warn`    amber-to-orange glow, warning triangle, "at risk" count
   * - `danger`  red glow with pulsing halo — any seat within 24h of forfeit
   *
   * The parent (`LobbyNav`) reads this from `getTileAlertStatus()` and
   * forwards it here so this component stays display-only.
   */
  alertRisk?: 'safe' | 'warn' | 'danger';
  /** Number of seats in the worst tier — rendered alongside the triangle. */
  alertCount?: number;
  /** Shortest runway (days) across all seats — used in the tooltip copy. */
  minRunwayDays?: number;
  /** When true, show a connect CTA instead of tile totals (guest mode). */
  showConnectWallet?: boolean;
  onConnectWallet?: () => void;
  connectDisabled?: boolean;
  className?: string;
}

/**
 * Header pill that summarises the viewer's tile portfolio.
 *
 * Tapping the pill routes to `/tiles` — the standalone cross-board
 * tile management page — instead of the old `TileManagementModal`.
 * The page replaces the modal, so we no longer need the
 * `OPEN_TILE_MANAGEMENT_EVENT` window dispatch path; everything that
 * previously listened for that event now navigates the user directly
 * to `/tiles`.
 */
export function TileValueNav({
  tileValue,
  alertRisk = 'safe',
  alertCount = 0,
  minRunwayDays,
  showConnectWallet = false,
  onConnectWallet,
  connectDisabled = false,
  className,
}: TileValueNavProps) {
  const router = useRouter();

  const numericValue = parseFloat(tileValue.replace(/,/g, '')) || 0;
  const compactUsd = formatCompactUsd(numericValue);

  const isDanger = alertRisk === 'danger';
  const isWarn = alertRisk === 'warn';
  const isAlert = isDanger || isWarn;

  // Tooltip folds the runway warning in when anything is at risk so the
  // header telegraphs the urgency without needing the user to click in.
  const runwayHint =
    typeof minRunwayDays === 'number' && minRunwayDays <= 7
      ? ` · min runway ${minRunwayDays < 1 ? `${Math.round(minRunwayDays * 24)}h` : `${Math.round(minRunwayDays)}d`}`
      : '';
  const title = isAlert
    ? `Tiles · ${compactUsd} — ${alertCount} at risk${runwayHint}`
    : `Tiles · ${compactUsd} — click to manage your seats`;

  if (showConnectWallet) {
    return (
      <button
        type="button"
        onClick={onConnectWallet}
        disabled={connectDisabled}
        className={cn(
          'relative h-7 pl-2 pr-2.5 flex items-center gap-1.5 rounded-[4px] border text-[10px] font-mono uppercase tracking-[0.15em] transition-colors',
          'bg-gradient-to-b from-amber-500/15 to-amber-500/5 border-amber-400/40 text-amber-100 hover:border-amber-300/70 hover:from-amber-500/25',
          connectDisabled && 'opacity-50 pointer-events-none',
          className,
        )}
        aria-label="Connect wallet to view tiles"
        title="Connect wallet to view your tiles"
      >
        <LayoutGrid className="h-3 w-3 text-amber-300" />
        <span className="hidden sm:inline">Tiles</span>
        <span className="tabular-nums text-zinc-400">—</span>
        <span className="text-[9px] normal-case tracking-normal text-zinc-500">connect</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => router.push('/tiles')}
      className={cn(
        'relative h-7 pl-2 pr-2.5 flex items-center gap-1.5 rounded-[4px] border text-[10px] font-mono uppercase tracking-[0.15em] transition-colors',
        isDanger
          ? 'bg-gradient-to-b from-red-500/25 to-red-500/10 border-red-400/60 text-red-100 hover:border-red-300/80 hover:from-red-500/35 shadow-[0_0_12px_-2px_rgba(239,68,68,0.45)]'
          : isWarn
            ? 'bg-gradient-to-b from-orange-500/20 to-orange-500/5 border-orange-400/50 text-orange-100 hover:border-orange-300/70 hover:from-orange-500/30'
            : 'bg-gradient-to-b from-amber-500/15 to-amber-500/5 border-amber-400/40 text-amber-100 hover:border-amber-300/70 hover:from-amber-500/25',
        className,
      )}
      aria-label="Your tiles"
      title={title}
    >
      {isAlert ? (
        <AlertTriangle
          className={cn('h-3 w-3', isDanger ? 'text-red-300' : 'text-orange-300')}
        />
      ) : (
        <LayoutGrid className="h-3 w-3 text-amber-300" />
      )}
      <span className="hidden sm:inline text-zinc-400">Tiles</span>
      <span className="tabular-nums">{compactUsd}</span>
      {isAlert && alertCount > 0 && (
        <span
          className={cn(
            'ml-0.5 inline-flex items-center h-3.5 min-w-[14px] px-1 rounded-[2px] text-[8.5px] font-bold font-mono border tabular-nums',
            isDanger
              ? 'bg-red-500/20 border-red-400/50 text-red-100'
              : 'bg-orange-500/20 border-orange-400/50 text-orange-100',
          )}
          aria-hidden
        >
          {alertCount}
        </span>
      )}
      {isDanger && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[4px] animate-pulse"
          style={{
            boxShadow: '0 0 0 1px rgba(239,68,68,0.45), 0 0 12px -1px rgba(239,68,68,0.5)',
          }}
        />
      )}
    </button>
  );
}
